// Module //
//////////////////

// Warms the browser cache for images that aren't in the DOM yet, so that by the
// time they are inserted they resolve from cache instead of the network.
//
// Requests are issued at low priority and only while the main thread is idle, so
// they yield to anything the user is actually looking at.

const requested = new Set();
const onIdle = window.requestIdleCallback ? window.requestIdleCallback.bind(window) : (cb) => setTimeout(() => cb({ timeRemaining: () => 0 }), 200);
const cancelIdle = window.cancelIdleCallback ? window.cancelIdleCallback.bind(window) : clearTimeout;

// Speculative traffic isn't worth it on metered or slow connections.
const shouldPrefetch = () => {
  const connection = navigator.connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  return !["slow-2g", "2g"].includes(connection.effectiveType);
};

export default class ImagePrefetcher {
  constructor({ concurrency = 2 } = {}) {
    this._concurrency = concurrency;
    this._queue = [];
    this._active = 0;
    this._idleHandle = null;
    this._destroyed = false;
  }

  // URLs are consumed in the order given — callers decide the priority.
  add(urls) {
    urls.forEach((url) => {
      if (!url || requested.has(url)) return;
      requested.add(url);
      this._queue.push(url);
    });
  }

  start() {
    if (this._destroyed || this._idleHandle !== null) return;
    if (!shouldPrefetch()) return;
    this._schedule();
  }

  destroy() {
    this._destroyed = true;
    if (this._idleHandle !== null) cancelIdle(this._idleHandle);
    this._idleHandle = null;
    this._queue = [];
  }

  _schedule() {
    if (this._destroyed || !this._queue.length) return;

    this._idleHandle = onIdle(() => {
      this._idleHandle = null;
      while (this._active < this._concurrency && this._queue.length) this._fetchNext();
    });
  }

  _fetchNext() {
    const url = this._queue.shift();
    if (!url) return;

    this._active++;

    const image = new Image();
    image.fetchPriority = "low";
    image.decoding = "async";

    const done = () => {
      this._active--;
      if (this._idleHandle === null) this._schedule();
    };

    image.onload = done;
    image.onerror = done;
    image.src = url;
  }

}
